/**
 * Shared, structured error logging for the plugins.
 *
 * Every error flows through {@link logError}, which builds a consistent record
 * (app, phase, message, stack, context, timestamp) and:
 *   1. logs it to the console in a single structured call, and
 *   2. forwards it to an optional reporter sink (see {@link setErrorReporter}),
 *      so an app with a backend — e.g. pdf-to-figma — can persist it remotely.
 *
 * `installGlobalErrorHandlers` additionally captures uncaught errors and
 * unhandled promise rejections, so nothing fails silently in the plugin UI.
 */

export interface ErrorContext {
  /** Which plugin reported the error, e.g. "illustrator-to-figma". */
  app: string;
  /** Where it happened: "render", "import", "uncaught", "unhandledrejection", … */
  phase?: string;
  /** Any extra structured context (file name, mode, component stack, …). */
  [key: string]: unknown;
}

export interface ErrorRecord {
  app: string;
  phase: string;
  name: string;
  message: string;
  stack?: string | undefined;
  context: Record<string, unknown>;
  /** ISO timestamp. */
  timestamp: string;
  userAgent?: string | undefined;
}

export type ErrorReporter = (record: ErrorRecord) => void;

let reporter: ErrorReporter | null = null;

/**
 * Register a sink that receives every {@link ErrorRecord} (e.g. POST it to a
 * backend). Pass `null` to clear. The sink is called inside a try/catch so a
 * failing reporter can never break the app.
 */
export function setErrorReporter(fn: ErrorReporter | null): void {
  reporter = fn;
}

/** Stringify any value for a message, never throwing (circular/BigInt/undefined). */
function describe(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/** Log an error through the shared pipeline. Returns the structured record. */
export function logError(error: unknown, context: ErrorContext): ErrorRecord {
  const err = error instanceof Error ? error : new Error(describe(error));

  const { app, phase = 'error', ...rest } = context;

  const record: ErrorRecord = {
    app,
    phase,
    name: err.name || 'Error',
    message: err.message || String(error),
    stack: err.stack,
    context: rest,
    timestamp: new Date().toISOString(),
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };

  // One structured console call (message line + the full record for drill-down).
  console.error(`[${record.app}] ${record.phase}: ${record.message}`, record);

  try {
    reporter?.(record);
  } catch {
    // A logging sink must never throw into the app.
  }

  return record;
}

/**
 * Capture uncaught errors and unhandled promise rejections for `app`. Returns a
 * disposer that removes the listeners. No-op outside a browser (e.g. unit tests).
 */
export function installGlobalErrorHandlers(context: { app: string }): () => void {
  if (typeof window === 'undefined') return () => {};

  const onError = (event: ErrorEvent) => {
    logError(event.error ?? event.message, {
      app: context.app,
      phase: 'uncaught',
    });
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    logError(event.reason, { app: context.app, phase: 'unhandledrejection' });
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
